/**
 * Campo de empresas da carteira: escolher várias empresas pelo código do Domínio, pela
 * razão social ou pelo CNPJ, com ou sem pontuação. Sem React, para ter teste.
 */

export interface IEmpresaDaBusca {
  id: number;
  external_id: number;
  razao: string;
  cnpj: string | null;
}

/** O que o campo mostra em cada empresa: a troca RAZÃO · CNPJ · ID da origem. */
export type ModoDaEmpresa = 'razao' | 'cnpj' | 'id';

export const soDigitos = (texto: string | null | undefined): string =>
  String(texto ?? '').replace(/\D/g, '');

/** 12345678000195 → 12.345.678/0001-95; o que não tem 14 dígitos fica como veio. */
export const formatarCnpj = (cnpj: string | null | undefined): string => {
  const d = soDigitos(cnpj);
  if (d.length !== 14) return cnpj ?? '';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

export const rotuloDaEmpresa = (e: IEmpresaDaBusca, modo: ModoDaEmpresa): string => {
  if (modo === 'id') return String(e.external_id);
  if (modo === 'cnpj') return formatarCnpj(e.cnpj) || String(e.external_id);
  return e.razao;
};

/**
 * Vários termos colados de uma vez: vírgula, ponto e vírgula, tabulação ou quebra de
 * linha separam. O espaço não separa, porque está dentro do nome.
 */
export const separarTermos = (texto: string): string[] =>
  texto
    .split(/[,;\t\r\n]+/)
    .map(t => t.trim())
    .filter(Boolean);

/**
 * Qual das sugestões o termo escolhe sozinho, ao colar: o código ou o CNPJ exatos, ou a
 * única sugestão. Com mais de uma e nenhuma exata, a pessoa escolhe na lista.
 */
export const escolhaDoTermo = (
  termo: string,
  sugestoes: IEmpresaDaBusca[],
): IEmpresaDaBusca | null => {
  const d = soDigitos(termo);
  const exata = d
    ? sugestoes.find(e => String(e.external_id) === d || soDigitos(e.cnpj) === d)
    : undefined;
  if (exata) return exata;
  return sugestoes.length === 1 ? sugestoes[0] : null;
};

/** Junta sem repetir a mesma empresa. */
export const juntarEmpresas = (
  atuais: IEmpresaDaBusca[],
  novas: IEmpresaDaBusca[],
): IEmpresaDaBusca[] => {
  const vistos = new Set(atuais.map(e => e.external_id));
  return [
    ...atuais,
    ...novas.filter(e => {
      if (vistos.has(e.external_id)) return false;
      vistos.add(e.external_id);
      return true;
    }),
  ];
};
